import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { replace } from 'react-router-redux';
import lodash, { isEqual, sortBy } from 'lodash';
import { jsonHelper } from '@complion/query';
import { getPredicateGroups } from 'binder/util/PredicateGroupingVisitor';
import {
    rootQueryNodeSelector,
    binderSetupFieldsSelector,
    binderLoadedSelector,
    binderSetupDescriptorsSelector,
    binderSetupErrorsSelector,
    binderSavingSelector,
    dataSourceIdSelector,
    dataSourceTypeSelector,
    binderSetupDataSourceItemsSelector,
    binderNotifiedUsersSelector,
    binderNotifiedUsersLoadedSelector
} from 'binder/selectors/binderSetup';
import {
    loadBinderSetupFields,
    binderSetupFieldValueChange,
    saveBinderQuery,
    writeSetupErrorMessages,
    writeBinderSaving,
    resetBinderSetupFieldValues,
    displayBinderActivationError,
    loadBinderNotifiedUsers,
    saveBinderNotifiedUsers
} from 'binder/actions/binderSetup';
import { loadUsersConfiguration, unloadUsers } from 'binder/actions/usersConfiguration';

import styles from './BinderSetup.scss';
import LoadingComponent from 'common/permissions/components/LoadingComponent';
import FormField from 'binder/components/BinderSetup/FormField/FormField';
import MultiselectList from 'binder/components/BinderSetup/ValueSets/MultiselectList/MultiselectList';


import {
    editBinderQueryPredicate,
} from 'common/permissions/predicates';

const SavingBinder = 'save';
const ActivatingBinder = 'activate';


function ActiveBinderToggle({ binder, disableButton, savingBinder, activateButtonStyle, onSubmitActivateBinder }) {
    if (lodash.get(binder, 'status', '').toLowerCase() === 'new') {
        return (
            <button
                onClick={onSubmitActivateBinder}
                disabled={disableButton}
                className={activateButtonStyle}
                data-cy="ActiveBinderToggle"
            >
                {savingBinder === ActivatingBinder ? 'Activating Binder' : 'Activate Binder'}
            </button>
        );
    }
    return null;
}

function NotifyUsersView({ actionType, selectedNotifiedUsers, availableIntenalUsers, onChangeNotifiedUsers }) {
    if (actionType !== 'BINDER_SETUP') return null;
    return (
        <div className={styles.notifyUsers} data-cy="NotifyUsersView">
            <div className={styles.notifyUsersTitle}>
                <label className={styles.notifyUsersLabel} htmlFor="notifyUsersIntegration">
                    Notify Users (Integration)
                </label>
                <span className={styles.notifyUsersDescription}>
                    Select one or multiple values from the list
                </span>
            </div>
            <div className={styles.notifyUsersField}>
                <MultiselectList
                    dataType="small_text"
                    value={selectedNotifiedUsers}
                    availableValues={availableIntenalUsers}
                    changeHandler={onChangeNotifiedUsers}
                />
            </div>
        </div>
    );
}
function FormFields({
    fields,
    onFieldChanged,
    descriptors,
    setupFieldErrors,
    shouldScrollFieldIntoView,
    fieldToScrollIntoView
}) {
    return (
        <div className={styles.formContainer} data-cy="FormFields">
            {fields.map(field =>
                <FormField
                    key={field.groupId}
                    shouldScrollFieldIntoView={shouldScrollFieldIntoView}
                    fieldToScrollIntoView={fieldToScrollIntoView}
                    validationErrors={setupFieldErrors[field.groupId]}
                    onChange={onFieldChanged.bind(this, field.groupId)}
                    field={field}
                    descriptor={lodash.get(descriptors, field.predicateGroupKey.descriptorId)} />
            )}
        </div>
    );
}

/**
 * This component is being used for the BinderSetup and for the BinderSetupStep for the admin
 * view to edit a binder template.
 */
export class BinderSetup extends Component {

    constructor(props) {
        super(props);
        this.predicateGroups = null;
        this.state = {
            shouldScrollFieldIntoView: false,
            fieldToScrollIntoView: null,
            shouldShowActivateButton: false,
            fieldErrors: [],
            selectedNotifiedUsers: [],
            availableInternalUsers: [],
            showNotifyUsers: false
        };
    }

    componentDidMount() {
        const { binder, users, loadUsersConfiguration, loadBinderNotifiedUsers, actionType } = this.props;

        this.loadBinderSetupFields();

        if (actionType === 'BINDER_SETUP') {
            if (lodash.isNil(users) || lodash.isEmpty(users)) {
                loadUsersConfiguration(binder.id, { includeSelf: true });
            } else {
                this.calculateAvailableInternalUsers();
            }
            loadBinderNotifiedUsers(binder.id);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.rootQueryNode !== this.props.rootQueryNode) {
            this.loadBinderSetupFields();
        }

        if (!lodash.isEqual(prevProps.setupFieldErrors, this.props.setupFieldErrors)) {
            this.setFieldToScrollIntoView();
        }

        if (this.props.shouldRedirect) {
            this.redirectToBinderFiles();
        }

        if (!isEqual(prevProps.users, this.props.users) && !lodash.isEmpty(this.props.users)) {
            this.calculateAvailableInternalUsers();
        }

        if (!prevProps.notifiedUsersLoaded && this.props.notifiedUsersLoaded) {
            this.setState({
                selectedNotifiedUsers: this.props.notifiedUsers.map(user => user.displayName)
            });
        }
    }

    componentWillUnmount() {
        const { actionType, unloadUsers } = this.props;

        if (actionType === 'BINDER_SETUP') {
            unloadUsers();
        }
    }

    redirectToBinderFiles() {
        const {
            replace,
            binder: {
                id: binderId
            }
        } = this.props;

        replace(`/binder/${binderId}/files`);
    }

    setFieldToScrollIntoView() {
        const {
            setupFieldErrors,
            fields
        } = this.props;

        const field = lodash.find(fields, f => Boolean(setupFieldErrors[f.groupId]));

        this.setState({
            shouldScrollFieldIntoView: true,
            fieldToScrollIntoView: field
        }, this.setState({ shouldScrollFieldIntoView: false }));
    }

    loadBinderSetupFields() {
        const {
            rootQueryNode,
            loadBinderSetupFields
        } = this.props;

        if (!rootQueryNode) {
            return;
        }

        getPredicateGroups(rootQueryNode)
            .then(predicateGroups => {
                // store these on the component instance so a
                // group can be looked up later to save the query by
                // the key property on this.props.fields
                this.predicateGroups = predicateGroups;
                loadBinderSetupFields(predicateGroups);
            });
    }

    trimValueIfString(value) {
        if (lodash.isString(value)) {
            return value.trim();
        }
        return value;
    }

    sanitizeFieldValues(values) {
        if (Array.isArray(values)) {
            return values.map(value => {
                return this.trimValueIfString(value);
            });
        }
        return this.trimValueIfString(values);
    }

    writePredicateGroupValues(predicateGroups, fields) {
        for (const predicateGroup of predicateGroups.values()) {
            const field = lodash.find(fields, field => lodash.isEqual(field.predicateGroupKey, predicateGroup.key));
            if (!field) {
                throw new Error('cannot find field for predicate group');
            }
            if (field[field.dataHandlerProperty] === field.defaultValue) {
                continue;
            }
            field[field.dataHandlerProperty] = this.sanitizeFieldValues(field[field.dataHandlerProperty]);
            const { saveValuePropertyPath } = predicateGroup;
            const { dataHandlerProperty } = field;
            const subPath = lodash.toPath(saveValuePropertyPath);
            const path = [...subPath, dataHandlerProperty];
            for (const queryNode of predicateGroup.queryNodes) {
                lodash.set(queryNode, path, lodash.clone(field[dataHandlerProperty]));
            }
        }
    }

    resetFieldValues() {
        const { fields } = this.props;
        const predicateGroupsValues = Array.from(this.predicateGroups.values());
        const predicateGroups = lodash.map(predicateGroupsValues, (predicateGroup) => {
            const field = lodash.find(fields, field => lodash.isEqual(field.predicateGroupKey, predicateGroup.key));
            predicateGroup.value = field.defaultValue || null;
            return { ...predicateGroup };
        });

        this.props.resetBinderSetupFieldValues(predicateGroups);
    }

    async onSubmitSaveQuery({ binderStatus = 'NEW', saveType = SavingBinder }) {
        binderStatus = binderStatus.toUpperCase();
        const {
            fields,
            writeBinderSaving,
            writeSetupErrorMessages,
            actionType,
            rootQueryNode,
            saveBinderQuery,
        } = this.props;

        writeBinderSaving(saveType);
        writeSetupErrorMessages(null);

        // mutates rootQueryNode through references found in predicateGroups
        this.writePredicateGroupValues(this.predicateGroups, fields);

        const serializedRootQueryNode = jsonHelper.stringify(rootQueryNode);
        saveBinderQuery(this.props.binder, serializedRootQueryNode, binderStatus);

        if (actionType === 'BINDER_SETUP') {
            this.saveBinderNotifiedUsers();
        }
    }

    async onSubmitActivateBinder() {
        const binderStatus = 'ACTIVE';
        await this.onSubmitSaveQuery({ binderStatus, saveType: ActivatingBinder });
    }

    // returns queryValidationErrors as plain objects
    //  keyed by groupId of predicate group that contains the node
    collectValidationErrors(validationResult) {
        const { errors } = validationResult;
        const predicateGroups = Array.from(this.predicateGroups.values());
        const messageGroups = lodash.groupBy(errors, error => {
            const matchingPredicateGroup = lodash.find(predicateGroups, predicateGroup => {
                return lodash.some(predicateGroup.queryNodes, queryNode => queryNode.nodeId === error.id);
            });
            return matchingPredicateGroup.key.groupId;
        });

        const plainMessageObjectsWithoutDuplicates = lodash.mapValues(messageGroups,
            (messageGroup, groupId) => {
                const uniqueMessages = lodash.uniqWith(messageGroup, (error1, error2) => {
                    return error1.message === error2.message &&
                        error1.type === error2.type &&
                        error1.property === error2.property;
                });
                return lodash.map(uniqueMessages,
                    ({ message, property, type }) => ({ groupId, message, property, type }));
            });
        return plainMessageObjectsWithoutDuplicates;
    }

    singleValueFieldValidate(fieldId, userInput, errorList, props) {
        const SINGLE_VALUE_TYPE = 'DataSourceSingleValue';

        const targetedDataSourceId = dataSourceIdSelector(props, fieldId);
        const targetedDataSourceType = dataSourceTypeSelector(props, fieldId);

        const dataSourceDictionary = lodash.get(props, `dataSourceItems.${targetedDataSourceId}`);

        const isSingleValueType = targetedDataSourceType === SINGLE_VALUE_TYPE;
        const isEmptyDataSource = lodash.isNil(dataSourceDictionary);


        if (isSingleValueType && !isEmptyDataSource) {
            //user input exist in data source dictionary
            const userInputExistInDataSource = !lodash.isNil(dataSourceDictionary.find(option => option.value === userInput));

            //add or remove the error if user input exist in dataSource dictionary
            if (userInputExistInDataSource) {
                lodash.remove(errorList, errorId => errorId === fieldId);
            } else {
                errorList = lodash.union([fieldId], errorList);
            }
        }

        return errorList;
    }

    onFieldChanged(groupId, value) {
        let newFieldErrors = lodash.get(this.state, 'fieldErrors');
        this.props.binderSetupFieldValueChange(groupId, value);
        this.setState({
            fieldErrors: this.singleValueFieldValidate(groupId, value, newFieldErrors, this.props)
        });
    }

    renderTitle() {
        const { title } = this.props;
        return title ? title : 'Binder Setup';
    }

    calculateAvailableInternalUsers() {
        const binderUsers = sortBy(Object.values(this.props.users), ['displayName'])
            .filter(user => user.binderPermissions.hasBinderAccess.value.toLowerCase() === 'yes' && user.isActive)
            .map((user, index) => ({
                value: user.displayName,
                text: user.emailAddress,
                dataType: 'small_text',
                order: index,
                sub: user.sub,
                emailAddress: user.emailAddress,
                id: user.id,
            }));

        this.setState({ availableInternalUsers: binderUsers });
    }

    onChangeNotifiedUsers(changedUsers) {
        this.setState({ selectedNotifiedUsers: changedUsers });
    }

    saveBinderNotifiedUsers() {
        const { binder, notifiedUsers, saveBinderNotifiedUsers } = this.props;
        const { selectedNotifiedUsers, availableInternalUsers } = this.state;
        const userIds = availableInternalUsers
            .filter(user => selectedNotifiedUsers.includes(user.value))
            .map(user => user.id);

        // Keep previous users if they are not removed even when they are not displayed in current available list
        const keptPreviousNotifiedUserIds = notifiedUsers
            .filter(user => selectedNotifiedUsers.includes(user.displayName) && !userIds.includes(user.id))
            .map(user => user.id);

        userIds.push(...keptPreviousNotifiedUserIds);

        saveBinderNotifiedUsers({
            binderId: binder.id,
            userIds,
        });
    }

    render() {
        const {
            loaded,
            fields,
            descriptors,
            setupFieldErrors,
            binder,
            savingBinder,
            actionType,
            stepContext: {
                isStudy
            } = {},
            binderPermissions,
            institutionPermissions,
        } = this.props;
        const {
            shouldScrollFieldIntoView,
            fieldToScrollIntoView,
            fieldErrors,
            selectedNotifiedUsers,
            availableInternalUsers,
        } = this.state;

        const disableButton = (Boolean(savingBinder) || fieldErrors.length > 0);
        const type = isStudy ? 'Study' : 'Binder';
        if (!loaded) {
            return <LoadingComponent />;
        }

        const canEditBinderQuery = binderPermissions ? editBinderQueryPredicate(binderPermissions) : institutionPermissions.complionAdmin;

        return (
            <div className={styles.container} data-cy="BinderSetup">
                <h2 className={styles.header} data-cy="BinderSetup-header">{this.renderTitle()}</h2>
                <FormFields
                    shouldScrollFieldIntoView={shouldScrollFieldIntoView}
                    fieldToScrollIntoView={fieldToScrollIntoView}
                    setupFieldErrors={setupFieldErrors}
                    fields={fields}
                    descriptors={descriptors}
                    onFieldChanged={this.onFieldChanged.bind(this)}
                />
                {this.state.showNotifyUsers &&
                    <NotifyUsersView
                        actionType={actionType}
                        selectedNotifiedUsers={selectedNotifiedUsers}
                        availableIntenalUsers={availableInternalUsers}
                        onChangeNotifiedUsers={this.onChangeNotifiedUsers.bind(this)}
                    />
                }

                {canEditBinderQuery &&
                    <div className={styles.actionGroup}>
                        <button
                            onClick={this.resetFieldValues.bind(this)}
                            disabled={Boolean(savingBinder)}
                            className={styles.resetButton}
                            data-cy="BinderSetup-reset"
                        >
                            Reset {type}
                        </button>
                        <button
                            onClick={this.onSubmitSaveQuery.bind(this, { binderStatus: binder.status })}
                            disabled={disableButton}
                            className={styles.saveButton}
                            data-cy="BinderSetup-SaveQuery"
                        >
                            {savingBinder === SavingBinder ? `Saving ${type}` : `Save ${type}`}
                        </button>
                        <ActiveBinderToggle
                            binder={binder}
                            disableButton={disableButton}
                            savingBinder={savingBinder}
                            activateButtonStyle={styles.activateButton}
                            onSubmitActivateBinder={this.onSubmitActivateBinder.bind(this)}
                        />
                    </div>
                }

            </div>
        );
    }
}

BinderSetup.propTypes = {
    binder: PropTypes.shape({
        id: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        layout: PropTypes.shape({
            folders: PropTypes.array.isRequired
        })
    })
};

export default connect(state => {
    return {
        binder: state.binder,
        binderPermissions: lodash.get(state, 'me.binderPermissions', {}),
        loaded: binderLoadedSelector(state),
        rootQueryNode: state.app.loadingBinder ? null : rootQueryNodeSelector(state),
        fields: binderSetupFieldsSelector(state),
        descriptors: binderSetupDescriptorsSelector(state),
        setupFieldErrors: binderSetupErrorsSelector(state),
        savingBinder: binderSavingSelector(state),
        dataSourceItems: binderSetupDataSourceItemsSelector(state),
        shouldRedirect: lodash.get(state, 'binder.setup.shouldRedirect'),
        users: state.users,
        notifiedUsers: binderNotifiedUsersSelector(state),
        notifiedUsersLoaded: binderNotifiedUsersLoadedSelector(state),
        actionType: 'BINDER_SETUP',
    };
}, {
    loadBinderSetupFields,
    binderSetupFieldValueChange,
    saveBinderQuery,
    writeSetupErrorMessages,
    resetBinderSetupFieldValues,
    displayBinderActivationError,
    writeBinderSaving,
    replace,
    loadUsersConfiguration,
    loadBinderNotifiedUsers,
    saveBinderNotifiedUsers,
    unloadUsers,
}
)(BinderSetup);
